import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, PushpinFilled } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { projectTemplates } from "../../lib/api";
import type { ProjectTemplate } from "../../lib/types";
import ProjectTemplateEditor from "./ProjectTemplateEditor";

/**
 * OAIP-44 — admin-only Settings tab. Lists the project plan template library,
 * lets admins create / set-default / delete templates, and opens the full
 * editor. Reads are live via `onSnapshot`; writes route through the audited
 * `projectTemplates` Admin-SDK handler so every change lands in `auditLog`.
 */
export default function ProjectTemplatesTab() {
  const { message, modal } = App.useApp();
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProjectTemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "projectTemplates"),
      (snap) => {
        setTemplates(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ProjectTemplate, "id">) })),
        );
        setLoading(false);
      },
      (err) => {
        message.error(err.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [message]);

  // Keep the open editor's metadata fresh as snapshots arrive.
  useEffect(() => {
    if (!editing) return;
    const latest = templates.find((t) => t.id === editing.id);
    if (latest && latest !== editing) setEditing(latest);
  }, [templates, editing]);

  const sorted = useMemo(
    () =>
      templates
        .slice()
        .sort(
          (a, b) =>
            Number(!!b.isDefault) - Number(!!a.isDefault) ||
            (a.name || "").localeCompare(b.name || ""),
        ),
    [templates],
  );

  const handleCreate = async () => {
    try {
      const v = await createForm.validateFields();
      setCreating(true);
      const res = await projectTemplates.create({
        name: v.name,
        description: v.description || "",
        isDefault: !!v.isDefault,
      });
      message.success("Template created.");
      setCreateOpen(false);
      createForm.resetFields();
      // Open the editor on the new template immediately.
      setEditing({
        id: res.id,
        name: v.name,
        description: v.description || "",
        isDefault: !!v.isDefault,
        version: 1,
      });
    } catch (err) {
      if (err instanceof Error && err.message) message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async (template: ProjectTemplate) => {
    try {
      await projectTemplates.update(template.id, { isDefault: true });
      message.success(`"${template.name}" is now the default template.`);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };

  const handleDelete = (template: ProjectTemplate) =>
    modal.confirm({
      title: `Delete template "${template.name}"?`,
      content: "This deletes the template and all its stages, tasks, subtasks, and metrics. Existing projects created from it are not affected.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await projectTemplates.delete(template.id);
          message.success("Template deleted.");
        } catch (err) {
          if (err instanceof Error) message.error(err.message);
        }
      },
    });

  const columns: ColumnsType<ProjectTemplate> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, row) => (
        <Space>
          <Typography.Text strong>{name}</Typography.Text>
          {row.isDefault && (
            <Tag icon={<PushpinFilled />} color="blue">
              Default
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (d?: string) => (
        <Typography.Text type="secondary" ellipsis style={{ maxWidth: 360 }}>
          {d || "—"}
        </Typography.Text>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 90,
      render: (v?: number) => `v${v ?? 1}`,
    },
    {
      title: "Last edited",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 170,
      render: (ts?: string) => (ts ? new Date(ts).toLocaleString() : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Tooltip title={row.isDefault ? "Already the default" : "Pin as the default template"}>
            <Button size="small" disabled={row.isDefault} onClick={() => handleSetDefault(row)}>
              Set default
            </Button>
          </Tooltip>
          <Tooltip title="Delete template">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete template ${row.name}`}
              onClick={() => handleDelete(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Project plan templates"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New template
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">
        Reusable project plans (stages, tasks, subtasks, metrics) applied when converting an intake
        to a project. The pinned default is pre-selected in the convert dialog.
      </Typography.Paragraph>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={sorted}
        pagination={false}
        locale={{ emptyText: "No templates yet. Create one to get started." }}
      />

      <Modal
        title="New project template"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="Create"
        okButtonProps={{ loading: creating }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Enter a template name" }]}>
            <Input placeholder="e.g. OAIP Standard AI Project" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea autoSize={{ minRows: 2 }} placeholder="When should this template be used?" />
          </Form.Item>
          <Form.Item label="Make this the default" name="isDefault" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {editing && (
        <ProjectTemplateEditor template={editing} open={!!editing} onClose={() => setEditing(null)} />
      )}
    </Card>
  );
}
